import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import BackToLifeGuide from "../components/BackToLifeGuide";

/**
 * Public SMS opt-in page — Twilio A2P 10DLC compliant.
 *
 * Compliance mechanics (enforced by this component AND not merely worded):
 *   - Phone number field is NOT required (no `required` attribute, no client-side
 *     enforcement) — the visitor can leave and take the "No thanks" path with an
 *     empty phone field.
 *   - Consent checkbox defaults to UNCHECKED.
 *   - "Sign up" submit button is disabled until the visitor VOLUNTARILY enters a
 *     phone number AND checks the consent box.
 *   - "No thanks — continue without text messages" is a prominent, always-enabled
 *     alternative that lets the visitor continue using LifeGuide immediately.
 *   - SMS enrollment is completely optional and is not a condition of using,
 *     purchasing, gifting, redeeming, or continuing any LifeGuide service.
 */
export default function SmsOptInPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const phoneEntered = phone.trim().length > 0;
  const canSubmit = phoneEntered && consent && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await publicApi.smsOptIn({
        phone: phone.trim(),
        sms_opt_in: true,
      });
      setDone(r);
      toast.success("You're signed up for LifeGuide SMS updates.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "We couldn't complete your opt-in. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  // "No thanks" — never sends anything to the server. Just returns the visitor
  // to the free LifeGuide experience with no phone, no consent, no opt-in.
  const skip = () => {
    navigate("/welcome");
  };

  return (
    <div className="min-h-screen text-[#2E2E2E]" data-testid="sms-optin-page">
      {/* -------- Nav (public) -------- */}
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <BackToLifeGuide testid="sms-back-to-lifeguide" />
            <Link
              to="/welcome"
              data-testid="sms-nav-home-link"
              className="flex items-center gap-2.5"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
                <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
              </div>
              <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
            </Link>
          </div>
          <Link
            to="/login"
            data-testid="sms-nav-signin"
            className="text-sm text-[#6B6B6B] hover:text-[#4F6F52]"
          >
            Sign in
          </Link>
        </div>
      </header>

      <section className="max-w-2xl mx-auto px-6 pt-14 sm:pt-20 pb-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#E1ECDD] border border-[#4F6F52]/20 px-4 py-1.5 mb-6">
          <MessageSquare size={13} className="text-[#4F6F52]" />
          <span className="text-xs uppercase tracking-[0.18em] text-[#4F6F52] font-semibold">
            Optional SMS updates
          </span>
        </div>
        <h1
          data-testid="sms-optin-heading"
          className="font-display text-4xl sm:text-5xl leading-[1.05] text-[#2E2E2E] mb-5"
        >
          Optional Text Messages
        </h1>
        <p
          data-testid="sms-optin-explanation"
          className="text-[17px] text-[#4E5A4C] leading-relaxed mb-4"
        >
          Get optional informational and service-related LifeGuide text
          messages, such as requested guidance notifications, reflection
          reminders, gift-related service notifications, and account
          notifications.
        </p>

        {/* Prominent "not required" statement immediately above the form. */}
        {!done && (
          <div
            data-testid="sms-not-required-notice"
            className="rounded-2xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/60 px-5 py-4 mb-6"
          >
            <p className="text-[15px] text-[#2E2E2E] leading-relaxed">
              <strong>You can use all LifeGuide services available to your
              account without signing up for text messages.</strong>{" "}
              You don't need to enter a phone number or check the consent box
              below unless you want SMS updates.
            </p>
          </div>
        )}

        {done ? (
          <div
            data-testid="sms-optin-success"
            className="rounded-3xl border border-[#4F6F52]/25 bg-[#E1ECDD]/60 p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={18} className="text-[#4F6F52]" />
              <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#4F6F52]">
                You're signed up
              </span>
            </div>
            <p className="text-[16px] text-[#2E2E2E] leading-relaxed mb-2">
              We'll send LifeGuide SMS updates to{" "}
              <strong>{done.phone}</strong>.
            </p>
            <p className="text-sm text-[#4E5A4C] leading-relaxed mb-5">
              Reply <strong>STOP</strong> at any time to opt out or{" "}
              <strong>HELP</strong> for help. Message frequency may vary and
              message and data rates may apply.
            </p>
            <Link
              to="/welcome"
              data-testid="sms-optin-success-continue"
              className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 font-medium transition"
            >
              Continue to LifeGuide <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            <form
              onSubmit={submit}
              data-testid="sms-optin-form"
              // `noValidate` prevents the browser from enforcing native
              // required-attribute or type=tel validation before our own
              // "canSubmit" gate runs — reinforces that phone entry is
              // purely optional.
              noValidate
              className="rounded-3xl border border-[#DCE5DB] bg-white p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)] space-y-5"
            >
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">
                  Mobile number{" "}
                  <span className="text-[#4F6F52] font-normal">
                    (optional — only if you want SMS)
                  </span>
                </label>
                <input
                  data-testid="sms-optin-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="(555) 123-4567"
                  aria-required="false"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  data-testid="sms-optin-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-[#DCE5DB] text-[#4F6F52] focus:ring-[#4F6F52]/30 accent-[#4F6F52]"
                />
                <span
                  data-testid="sms-optin-disclosure"
                  className="text-sm text-[#2E2E2E] leading-relaxed"
                >
                  I agree to receive recurring informational and
                  service-related SMS messages from LifeGuide, including
                  requested guidance notifications, reflection reminders,
                  gift-related service notifications, and account
                  notifications, at the mobile number I provide. Message
                  frequency varies. Message and data rates may apply. Reply{" "}
                  <strong>STOP</strong> to opt out and <strong>HELP</strong>{" "}
                  for help. Consent is not a condition of purchase or use of
                  LifeGuide.
                </span>
              </label>

              <p className="text-xs text-[#6B6B6B] leading-relaxed">
                Review our{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener"
                  data-testid="sms-optin-terms-link"
                  className="text-[#4F6F52] underline underline-offset-2 hover:text-[#3F5A42]"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener"
                  data-testid="sms-optin-privacy-link"
                  className="text-[#4F6F52] underline underline-offset-2 hover:text-[#3F5A42]"
                >
                  Privacy Policy
                </Link>
                .
              </p>

              <button
                data-testid="sms-optin-submit"
                type="submit"
                disabled={!canSubmit}
                aria-disabled={!canSubmit}
                title={
                  !phoneEntered
                    ? "Enter a phone number if you'd like SMS"
                    : !consent
                    ? "Check the consent box to opt in"
                    : ""
                }
                className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3.5 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
              >
                {busy ? "Signing you up…" : "Sign Up for SMS Updates"}
              </button>
            </form>

            {/* Skip / bypass option — visually distinct, always available,
                does NOT touch the server, does NOT require phone or consent. */}
            <div
              data-testid="sms-skip-block"
              className="mt-6 rounded-3xl border border-[#DCE5DB] bg-[#F9FAF7] p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="text-[15px] text-[#4E5A4C] leading-relaxed text-center sm:text-left">
                Don't want text messages? That's completely fine — LifeGuide
                works exactly the same for you.
              </div>
              <button
                type="button"
                onClick={skip}
                data-testid="sms-skip-btn"
                className="shrink-0 inline-flex items-center gap-2 bg-white border-2 border-[#4F6F52] text-[#4F6F52] hover:bg-[#EEF3EC] rounded-full px-5 py-3 font-medium transition"
              >
                No thanks — continue without text messages
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
      </section>

      {/* -------- Footer -------- */}
      <footer className="border-t border-[#DCE5DB] py-8 bg-white/50">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-[#6B6B6B] flex flex-wrap justify-center gap-x-5 gap-y-1">
          <Link to="/gift" className="hover:text-[#4F6F52]">
            Give LifeGuide
          </Link>
          <Link
            to="/sms"
            className="hover:text-[#4F6F52]"
            data-testid="footer-sms-link"
          >
            SMS Updates
          </Link>
          <Link to="/privacy" className="hover:text-[#4F6F52]">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[#4F6F52]">
            Terms
          </Link>
        </div>
      </footer>
    </div>
  );
}
